import { useEffect, useMemo, useState } from "react";
import {
  fetchPublicWorkspaceMetadata,
  type PublicWorkspaceMetadata,
} from "@ano/shared/api/public-workspaces";
import { parseAppPath, workspaceSlugFromUrl } from "@/lib/app-route";
import { API_URL } from "@/lib/config";

export interface AnonymousLanding {
  /**
   * Workspace slug from the URL — the path segment (`/acme`, canonical) or the
   * legacy subdomain host — or null when there's no workspace (home, root, or
   * localhost, where slug detection is intentionally off).
   */
  slug: string | null;
  /**
   * Channel name from a `/c/<channel>` (or `…/m/<id>`) deep link, or null. Used
   * by `AuthGate` to decide whether to focus a single channel/question surface.
   */
  channelName: string | null;
  /**
   * Anchored message id from a `…/c/<channel>/m/<id>` question deep link, or
   * null. Present only alongside `channelName`; drives the focused
   * single-question view.
   */
  messageId: string | null;
  status: "idle" | "loading" | "ready";
  meta: PublicWorkspaceMetadata | null;
}

export const ANONYMOUS_LANDING_LOOKUP_TIMEOUT_MS = 8000;

/**
 * Phase 5 — resolves whether a logged-OUT visitor on a public-workspace URL
 * (path `/acme` or legacy `acme.ano.dev`) should see the anonymous public view
 * (`AuthGate` renders it when `meta.anonymous_view` is true).
 *
 * REST-only: the unauthenticated `/public` endpoint, no Zero, no token. The
 * lookup only fires when `enabled` (the caller passes `!user && !isLoading`),
 * so signed-in loads never pay for it. While in flight, `status` is `loading`
 * so the caller can hold a loader instead of flashing the sign-in wall.
 */
export function useAnonymousLanding(enabled: boolean): AnonymousLanding {
  const slug = useMemo(() => {
    const s = workspaceSlugFromUrl(window.location);
    return s && s !== "home" ? s : null;
  }, []);
  // Deep-link targets, derived once from the same URL (no extra fetch). A
  // `…/c/<channel>/m/<id>` path yields both; `…/c/<channel>` yields only the
  // channel name; a bare `/<slug>` yields neither.
  const { channelName, messageId } = useMemo(() => {
    const route = parseAppPath();
    return { channelName: route.channelName, messageId: route.messageId };
  }, []);
  const [state, setState] = useState<{
    status: "idle" | "loading" | "ready";
    meta: PublicWorkspaceMetadata | null;
  }>(() => ({ status: slug ? "loading" : "idle", meta: null }));

  useEffect(() => {
    if (!enabled || !slug) return;
    let cancelled = false;
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (cancelled || settled) return;
      settled = true;
      setState({ status: "ready", meta: null });
    }, ANONYMOUS_LANDING_LOOKUP_TIMEOUT_MS);
    void fetchPublicWorkspaceMetadata({ apiBaseUrl: API_URL, slug })
      .then((meta) => {
        window.clearTimeout(timeout);
        if (cancelled || settled) return;
        settled = true;
        setState({ status: "ready", meta });
      })
      .catch(() => {
        window.clearTimeout(timeout);
        // Fail closed → falls through to the sign-in wall.
        if (cancelled || settled) return;
        settled = true;
        setState({ status: "ready", meta: null });
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [enabled, slug]);

  return { slug, channelName, messageId, ...state };
}
